import React, { useState, useEffect, useRef } from "react";
import { lookupDoi } from "../../services/bulkImportService";

// "https://doi.org/10.1/x", "doi:10.1/x", "DOI 10.1/x" -> "10.1/x"
const bareDoi = (doi) => (doi || '').trim()
    .replace(/^(https?:\/\/)?(dx\.)?doi\.org\//i, '')
    .replace(/^doi\s*:?\s*(?=10\.)/i, '');
const DOI_PATTERN = /^10\.\d{4,9}\/\S+$/;

const ORIGINS = [
    { value: 'LITERATURE', label: 'From a publication' },
    { value: 'OWN_LAB', label: 'Measured in my own lab' },
];

/**
 * The publication the runs come from, where in it the values are, and the
 * electrospinning device. A DOI fills in the title and abstract from Crossref.
 */
export default function ResearchMetadataForm({ onNext, onBack, initialValues, isRestricted }) {
    const [meta, setMeta] = useState({
        publicationTitle: "",
        doi: "",
        abstract: "",
        deviceManufacturer: "",
        deviceModel: "",
        customDevice: false,
        dataOrigin: "",
        extractionMethod: "",
        sourceLocation: "",
        diameterMethod: "",
        diameterNMeasured: "",
    });
    const [lookup, setLookup] = useState(null); // null | { state: 'busy' | 'ok' | 'error', text }
    const lastLookedUp = useRef(null);

    useEffect(() => {
        if (initialValues) {
            // customDevice is stored as text ("true"/"false"/"Yes"/"No"); the checkbox needs a boolean
            const custom = initialValues.customDevice;
            setMeta(prev => ({
                ...prev,
                ...Object.fromEntries(Object.entries(initialValues).filter(([, v]) => v !== null && v !== undefined)),
                customDevice: custom === true || custom === 'true' || custom === 'Yes',
            }));
        }
    }, [initialValues]);

    const set = (name, value) => setMeta(prev => ({ ...prev, [name]: value }));
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        set(name, type === "checkbox" ? checked : value);
    };

    /** Fills title and abstract from Crossref; fields the user already typed are kept. */
    const runLookup = async (raw, quiet = false) => {
        const doi = bareDoi(raw);
        if (!DOI_PATTERN.test(doi)) {
            if (!quiet) setLookup({ state: 'error', text: "That doesn't look like a DOI (e.g. 10.0000/example.2020.001)." });
            return;
        }
        if (quiet && lastLookedUp.current === doi) return;
        lastLookedUp.current = doi;
        setLookup({ state: 'busy', text: 'Looking up the publication…' });
        const found = await lookupDoi(doi);
        if (!found) {
            setLookup({ state: 'error', text: 'No publication found for this DOI on Crossref. Please check it, or fill in the title yourself.' });
            return;
        }
        setMeta(prev => ({
            ...prev,
            doi,
            publicationTitle: prev.publicationTitle?.trim() ? prev.publicationTitle : found.title,
            abstract: prev.abstract?.trim() ? prev.abstract : (found.abstract || prev.abstract),
            dataOrigin: prev.dataOrigin || 'LITERATURE',
        }));
        setLookup({ state: 'ok', text: 'Publication found. Title and abstract were filled in.' });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onNext({ ...meta, doi: bareDoi(meta.doi) });
    };

    return (
        <form onSubmit={handleSubmit} noValidate>
            <section className="cx-card" aria-labelledby="pub-title">
                <div className="cx-card__head">
                    <div>
                        <h2 id="pub-title">Publication</h2>
                        <p>Paste the DOI and the title and abstract are filled in for you. Unpublished data? Leave the DOI empty.</p>
                    </div>
                </div>
                <div className="cx-card__body">
                    <div className="cx-grid">
                        <div className="cx-field cx-span-2">
                            <label htmlFor="doi">DOI</label>
                            <div className="cx-inline">
                                <input id="doi" name="doi" className="cx-input" value={meta.doi}
                                    placeholder="10.xxxx/… or https://doi.org/10.xxxx/…"
                                    onChange={e => { handleChange(e); setLookup(null); }}
                                    onPaste={e => { const text = e.clipboardData.getData('text'); setTimeout(() => runLookup(text, true), 0); }}
                                    onBlur={e => { if (e.target.value.trim() && !meta.publicationTitle.trim()) runLookup(e.target.value, true); }}
                                    aria-describedby="doi-status" disabled={isRestricted} />
                                <button type="button" className="cx-btn cx-btn--secondary" onClick={() => runLookup(meta.doi)}
                                    disabled={!meta.doi?.trim() || lookup?.state === 'busy' || isRestricted}>
                                    {lookup?.state === 'busy' ? 'Looking up…' : 'Look up'}
                                </button>
                            </div>
                            <div id="doi-status" aria-live="polite"
                                className={`cx-help ${lookup?.state === 'ok' ? 'cx-help--ok' : lookup?.state === 'error' ? 'cx-help--error' : ''}`}>
                                {lookup ? lookup.text : 'Any form works: 10.xxxx/…, doi:10.xxxx/… or a doi.org link.'}
                            </div>
                        </div>
                        <div className="cx-field cx-span-2">
                            <label htmlFor="publicationTitle">Title</label>
                            <input id="publicationTitle" name="publicationTitle" className="cx-input" value={meta.publicationTitle}
                                onChange={handleChange} placeholder="Title of the publication, or a short name for unpublished work" />
                        </div>
                        <div className="cx-field cx-span-2">
                            <label htmlFor="abstract">Abstract <span className="cx-optional">optional</span></label>
                            <textarea id="abstract" name="abstract" className="cx-textarea" rows={4} value={meta.abstract}
                                onChange={handleChange} />
                        </div>
                    </div>
                </div>
            </section>

            <section className="cx-card" aria-labelledby="prov-title">
                <div className="cx-card__head">
                    <div>
                        <h2 id="prov-title">Where the values come from</h2>
                        <p>Applies to every run you add next. It helps others judge and reuse the data.</p>
                    </div>
                </div>
                <div className="cx-card__body">
                    <div className="cx-grid">
                        <div className="cx-field cx-span-2">
                            <span className="cx-label" id="origin-label">Origin</span>
                            <div className="cx-segmented" role="radiogroup" aria-labelledby="origin-label">
                                {ORIGINS.map(o => (
                                    <button key={o.value} type="button" role="radio" aria-checked={meta.dataOrigin === o.value}
                                        onClick={() => set('dataOrigin', meta.dataOrigin === o.value ? '' : o.value)}>
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="cx-field">
                            <label htmlFor="extractionMethod">The values were</label>
                            <select id="extractionMethod" name="extractionMethod" className="cx-select"
                                value={meta.extractionMethod || ''} onChange={handleChange}>
                                <option value="">Not specified</option>
                                <option value="REPORTED">Reported in the text or tables</option>
                                <option value="DIGITISED">Read from figures (digitised)</option>
                                <option value="ESTIMATED">Estimated</option>
                            </select>
                        </div>
                        <div className="cx-field">
                            <label htmlFor="sourceLocation">Where in the source <span className="cx-optional">optional</span></label>
                            <input id="sourceLocation" name="sourceLocation" className="cx-input" value={meta.sourceLocation || ''}
                                onChange={handleChange} placeholder="e.g. Table 2, Fig. 3b" maxLength={255} />
                        </div>
                        <div className="cx-field">
                            <label htmlFor="diameterMethod">Fiber diameter measured by</label>
                            <select id="diameterMethod" name="diameterMethod" className="cx-select"
                                value={meta.diameterMethod || ''} onChange={handleChange}>
                                <option value="">Not specified</option>
                                {['SEM', 'TEM', 'AFM', 'Optical microscopy', 'Other'].map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <div className="cx-field">
                            <label htmlFor="diameterNMeasured">Fibers measured per sample (n) <span className="cx-optional">optional</span></label>
                            <input id="diameterNMeasured" name="diameterNMeasured" type="number" min="1" step="1" className="cx-input"
                                value={meta.diameterNMeasured ?? ''} onChange={handleChange} placeholder="e.g. 100" />
                        </div>
                    </div>
                </div>
            </section>

            <section className="cx-card" aria-labelledby="device-title">
                <div className="cx-card__head">
                    <div>
                        <h2 id="device-title">Electrospinning device <span className="cx-optional">optional</span></h2>
                    </div>
                </div>
                <div className="cx-card__body">
                    <div className="cx-grid">
                        <div className="cx-field">
                            <label htmlFor="deviceManufacturer">Manufacturer</label>
                            <input id="deviceManufacturer" name="deviceManufacturer" className="cx-input" value={meta.deviceManufacturer || ''}
                                onChange={handleChange} placeholder="Manufacturer name" disabled={meta.customDevice} />
                        </div>
                        <div className="cx-field">
                            <label htmlFor="deviceModel">Model</label>
                            <input id="deviceModel" name="deviceModel" className="cx-input" value={meta.deviceModel || ''}
                                onChange={handleChange} placeholder="Model name or number" disabled={meta.customDevice} />
                        </div>
                        <label className="cx-check cx-span-2" htmlFor="customDevice">
                            <input type="checkbox" id="customDevice" name="customDevice" checked={meta.customDevice} onChange={handleChange} />
                            <span>Custom-built (DIY) setup</span>
                        </label>
                    </div>
                </div>
            </section>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-5">
                {onBack ? (
                    <button type="button" onClick={onBack} className="cx-btn cx-btn--ghost">
                        <i className="bi bi-arrow-left" aria-hidden="true"></i> Back
                    </button>
                ) : <span />}
                <button type="submit" className="cx-btn cx-btn--primary">
                    Continue to experimental data <i className="bi bi-arrow-right" aria-hidden="true"></i>
                </button>
            </div>
        </form>
    );
}
